'use client';

import React, { useState } from 'react';
import {
  ExtendedArchiMateDiagram,
  TechnicalDebt,
  DebtCategory,
  BusinessImpact,
} from '@/lib/archimate/extended-types';
import {
  createTechnicalDebt,
  calculateDiagramDebtScore,
  getHighPriorityDebt,
  getOverdueDebt,
  generateDebtReport,
  estimateDebtRemediationCost,
  prioritizeDebtRemediation,
  getDebtColor,
  exportDebtToCSV,
} from '@/lib/archimate/debt-utils';
import { X, AlertTriangle, DollarSign, Download, TrendingUp } from 'lucide-react';
import { PanelHelpDialog, HelpIconButton } from './PanelHelpDialog';

interface DebtPanelProps {
  diagram: ExtendedArchiMateDiagram;
  selectedElementId?: string;
  onUpdate: (diagram: ExtendedArchiMateDiagram) => void;
  onClose: () => void;
  onToggleHeatmap: (enabled: boolean) => void;
}

export default function DebtPanel({
  diagram,
  selectedElementId,
  onUpdate,
  onClose,
  onToggleHeatmap,
}: DebtPanelProps) {
  const [viewMode, setViewMode] = useState<'overview' | 'element' | 'prioritize'>('overview');
  const [showHeatmap, setShowHeatmap] = useState(false);

  const [elementDebt, setElementDebt] = useState<Omit<TechnicalDebt, 'id' | 'addedDate' | 'priority'>>({
    score: 5,
    category: 'legacy',
    description: '',
    estimatedEffort: '1 week',
    estimatedCost: undefined,
    currency: 'USD',
    businessImpact: 'medium',
    linkedIssues: [],
  });
  const [showHelp, setShowHelp] = useState(false);

  const overallScore = calculateDiagramDebtScore(diagram);
  const highPriority = getHighPriorityDebt(diagram, 4);
  const overdue = getOverdueDebt(diagram);
  const costEstimate = estimateDebtRemediationCost(diagram);
  const prioritized = prioritizeDebtRemediation(diagram);

  const handleSave = () => {
    if (!selectedElementId) return;

    const debt = createTechnicalDebt(
      elementDebt.description,
      elementDebt.category,
      elementDebt.score,
      elementDebt.businessImpact,
      elementDebt.estimatedEffort,
      elementDebt.estimatedCost,
      elementDebt.currency
    );

    const updatedDiagram = {
      ...diagram,
      elementMetadata: {
        ...diagram.elementMetadata,
        [selectedElementId]: {
          ...diagram.elementMetadata[selectedElementId],
          debt,
        },
      },
    };

    onUpdate(updatedDiagram);
  };

  const handleToggleHeatmap = () => {
    const newState = !showHeatmap;
    setShowHeatmap(newState);
    onToggleHeatmap(newState);
  };

  const handleExportReport = () => {
    const report = generateDebtReport(diagram);
    const blob = new Blob([report], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${diagram.name}-technical-debt.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleExportCSV = () => {
    const csv = exportDebtToCSV(diagram);
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${diagram.name}-technical-debt.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Load element debt if selected
  React.useEffect(() => {
    if (selectedElementId) {
      const metadata = diagram.elementMetadata[selectedElementId];
      if (metadata?.debt) {
        setElementDebt({
          score: metadata.debt.score,
          category: metadata.debt.category,
          description: metadata.debt.description,
          estimatedEffort: metadata.debt.estimatedEffort,
          estimatedCost: metadata.debt.estimatedCost,
          currency: metadata.debt.currency || 'USD',
          businessImpact: metadata.debt.businessImpact,
          linkedIssues: metadata.debt.linkedIssues,
        });
      } else {
        setElementDebt({
          score: 5,
          category: 'legacy',
          description: '',
          estimatedEffort: '1 week',
          estimatedCost: undefined,
          currency: 'USD',
          businessImpact: 'medium',
          linkedIssues: [],
        });
      }
      setViewMode('element');
    }
  }, [selectedElementId, diagram.elementMetadata]);

  const selectedElement = selectedElementId
    ? diagram.elements.find((e) => e.id === selectedElementId)
    : null;

  const getScoreColor = (score: number) => {
    if (score >= 8) return 'text-red-500';
    if (score >= 6) return 'text-accent';
    if (score >= 4) return 'text-yellow-500';
    return 'text-green-500';
  };

  return (
    <div className="fixed right-0 top-0 h-screen w-[550px] bg-background border-l border-border flex flex-col shadow-2xl z-50 animate-in slide-in-from-right duration-300">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-border">
        <div className="flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-accent" />
          <h2 className="text-xl font-bold text-foreground">Technical Debt</h2>
          <HelpIconButton onClick={() => setShowHelp(true)} />
        </div>
        <button onClick={onClose} className="p-2 hover:bg-cream/10 rounded transition-colors">
          <X className="w-5 h-5 text-foreground" />
        </button>
      </div>

      {/* Help Dialog */}
      {showHelp && (
        <PanelHelpDialog
          panelKey="debt"
          onClose={() => setShowHelp(false)}
        />
      )}

      {/* Toolbar */}
      <div className="p-4 border-b border-border space-y-3">
        <div className="flex gap-2">
          <button
            onClick={() => setViewMode('overview')}
            className={`px-4 py-2 rounded transition-colors ${
              viewMode === 'overview' ? 'bg-accent text-accent-foreground' : 'bg-cream/10 text-foreground hover:bg-cream/20'
            }`}
          >
            Overview
          </button>
          <button
            onClick={() => setViewMode('element')}
            disabled={!selectedElementId}
            className={`px-4 py-2 rounded transition-colors ${
              viewMode === 'element' ? 'bg-accent text-accent-foreground' : 'bg-cream/10 text-foreground hover:bg-cream/20'
            } disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            Element
          </button>
          <button
            onClick={() => setViewMode('prioritize')}
            className={`px-4 py-2 rounded transition-colors ${
              viewMode === 'prioritize' ? 'bg-accent text-accent-foreground' : 'bg-cream/10 text-foreground hover:bg-cream/20'
            }`}
          >
            Prioritize
          </button>
          <button
            onClick={handleToggleHeatmap}
            className={`ml-auto px-4 py-2 rounded transition-colors flex items-center gap-2 ${
              showHeatmap ? 'bg-accent text-accent-foreground' : 'bg-cream/10 text-foreground hover:bg-cream/20'
            }`}
          >
            <TrendingUp className="w-4 h-4" />
            Heatmap
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4">
        {/* Overview */}
        {viewMode === 'overview' && (
          <div className="space-y-6">
            {/* Overall Score */}
            <div className="p-6 bg-cream/5 border border-border rounded text-center">
              <div className="text-sm text-foreground-secondary mb-2">Average Debt Score</div>
              <div className={`text-5xl font-bold ${getScoreColor(overallScore)}`}>
                {overallScore.toFixed(1)}<span className="text-2xl">/10</span>
              </div>
            </div>

            {/* Cost Estimate */}
            <div className="p-4 bg-cream/5 border border-border rounded">
              <h3 className="text-lg font-bold text-foreground mb-3 flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-accent" />
                Remediation Cost Estimate
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-sm text-foreground-secondary">Total Hours</div>
                  <div className="text-2xl font-bold text-foreground">{costEstimate.totalHours.toFixed(0)}h</div>
                </div>
                <div>
                  <div className="text-sm text-foreground-secondary">Total Cost</div>
                  <div className="text-2xl font-bold text-accent">${costEstimate.totalCost.toLocaleString()}</div>
                </div>
              </div>
            </div>

            {/* High Priority Debt */}
            {highPriority.length > 0 && (
              <div>
                <h3 className="text-lg font-bold text-foreground mb-3 flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-red-500" />
                  High Priority Debt ({highPriority.length})
                </h3>
                <div className="space-y-2">
                  {highPriority.slice(0, 5).map(({ element, debt }) => (
                    <div key={element.id} className="p-3 bg-red-500/10 border border-red-500/30 rounded">
                      <div className="flex items-start justify-between mb-2">
                        <div className="font-medium text-foreground">{element.name}</div>
                        <div className="flex gap-0.5">
                          {[...Array(debt.priority)].map((_, i) => (
                            <span key={i} className="text-accent">⭐</span>
                          ))}
                        </div>
                      </div>
                      <div className="text-sm text-foreground-secondary mb-1">
                        {debt.category} • Score: {debt.score}/10 • Impact: {debt.businessImpact}
                      </div>
                      <div className="text-sm text-foreground/90">{debt.description}</div>
                      <div className="text-xs text-foreground-tertiary mt-2">
                        Effort: {debt.estimatedEffort}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Overdue Debt */}
            {overdue.length > 0 && (
              <div>
                <h3 className="text-lg font-bold text-foreground mb-3 flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-yellow-500" />
                  Overdue Items ({overdue.length})
                </h3>
                <div className="space-y-2">
                  {overdue.map(({ element, debt, daysOverdue }) => (
                    <div key={element.id} className="p-3 bg-yellow-500/10 border border-yellow-500/30 rounded">
                      <div className="font-medium text-foreground mb-1">{element.name}</div>
                      <div className="text-sm text-yellow-400">
                        {daysOverdue} days overdue • Target: {debt.targetResolutionDate}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-2">
              <button
                onClick={handleExportReport}
                className="flex-1 px-4 py-2 bg-cream/10 text-foreground rounded hover:bg-cream/20 flex items-center justify-center gap-2"
              >
                <Download className="w-4 h-4" />
                Export Report
              </button>
              <button
                onClick={handleExportCSV}
                className="flex-1 px-4 py-2 bg-cream/10 text-foreground rounded hover:bg-cream/20 flex items-center justify-center gap-2"
              >
                <Download className="w-4 h-4" />
                Export CSV
              </button>
            </div>
          </div>
        )}

        {/* Element Debt Editor */}
        {viewMode === 'element' && selectedElement && (
          <div className="space-y-4">
            <div className="p-4 bg-cream/5 border border-border rounded">
              <h3 className="font-bold text-foreground">{selectedElement.name}</h3>
              <p className="text-sm text-foreground-tertiary">
                {selectedElement.type} • {selectedElement.layer}
              </p>
            </div>

            <div className="space-y-3">
              {/* Debt Score Slider */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Debt Score: {elementDebt.score}/10
                </label>
                <input
                  type="range"
                  min="1"
                  max="10"
                  value={elementDebt.score}
                  onChange={(e) =>
                    setElementDebt({ ...elementDebt, score: parseInt(e.target.value) })
                  }
                  className="w-full h-2 bg-cream/20 rounded-lg appearance-none cursor-pointer accent-orange"
                />
                <div className="flex justify-between text-xs text-foreground-tertiary mt-1">
                  <span>Low</span>
                  <span>Medium</span>
                  <span>High</span>
                  <span>Critical</span>
                </div>
              </div>

              {/* Category */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Category</label>
                <select
                  value={elementDebt.category}
                  onChange={(e) =>
                    setElementDebt({ ...elementDebt, category: e.target.value as DebtCategory })
                  }
                  className="w-full px-4 py-2 bg-cream/10 border border-border rounded text-foreground focus:outline-none focus:border-accent"
                >
                  <option value="legacy">Legacy Code</option>
                  <option value="security">Security Vulnerability</option>
                  <option value="performance">Performance Issue</option>
                  <option value="maintainability">Poor Maintainability</option>
                  <option value="testing">Missing/Inadequate Tests</option>
                  <option value="documentation">Missing Documentation</option>
                </select>
              </div>

              {/* Business Impact */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Business Impact</label>
                <select
                  value={elementDebt.businessImpact}
                  onChange={(e) =>
                    setElementDebt({ ...elementDebt, businessImpact: e.target.value as BusinessImpact })
                  }
                  className="w-full px-4 py-2 bg-cream/10 border border-border rounded text-foreground focus:outline-none focus:border-accent"
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="critical">Critical</option>
                </select>
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Description</label>
                <textarea
                  value={elementDebt.description}
                  onChange={(e) =>
                    setElementDebt({ ...elementDebt, description: e.target.value })
                  }
                  placeholder="Describe the technical debt issue..."
                  rows={4}
                  className="w-full px-4 py-2 bg-cream/10 border border-border rounded text-foreground placeholder-foreground-muted focus:outline-none focus:border-accent resize-none"
                />
              </div>

              {/* Estimated Effort */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Estimated Effort</label>
                <input
                  type="text"
                  value={elementDebt.estimatedEffort}
                  onChange={(e) =>
                    setElementDebt({ ...elementDebt, estimatedEffort: e.target.value })
                  }
                  placeholder="e.g., 2 weeks, 3 sprints, 40 hours"
                  className="w-full px-4 py-2 bg-cream/10 border border-border rounded text-foreground placeholder-foreground-muted focus:outline-none focus:border-accent"
                />
              </div>

              {/* Estimated Cost */}
              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-foreground mb-2">Estimated Cost (Optional)</label>
                  <input
                    type="number"
                    value={elementDebt.estimatedCost || ''}
                    onChange={(e) =>
                      setElementDebt({
                        ...elementDebt,
                        estimatedCost: e.target.value ? parseFloat(e.target.value) : undefined
                      })
                    }
                    placeholder="e.g., 50000"
                    className="w-full px-4 py-2 bg-cream/10 border border-border rounded text-foreground placeholder-foreground-muted focus:outline-none focus:border-accent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Currency</label>
                  <select
                    value={elementDebt.currency || 'USD'}
                    onChange={(e) =>
                      setElementDebt({ ...elementDebt, currency: e.target.value })
                    }
                    className="w-full px-4 py-2 bg-cream/10 border border-border rounded text-foreground focus:outline-none focus:border-accent"
                  >
                    <option value="USD">USD</option>
                    <option value="EUR">EUR</option>
                    <option value="GBP">GBP</option>
                    <option value="JPY">JPY</option>
                    <option value="CAD">CAD</option>
                    <option value="AUD">AUD</option>
                    <option value="CHF">CHF</option>
                    <option value="CNY">CNY</option>
                    <option value="INR">INR</option>
                  </select>
                </div>
              </div>

              {/* Linked Issues */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Linked Issues (URLs)</label>
                <textarea
                  value={elementDebt.linkedIssues.join('\n')}
                  onChange={(e) =>
                    setElementDebt({
                      ...elementDebt,
                      linkedIssues: e.target.value.split('\n').filter((url) => url.trim()),
                    })
                  }
                  placeholder="https://github.com/org/repo/issues/123&#10;https://jira.company.com/browse/PROJ-456"
                  rows={3}
                  className="w-full px-4 py-2 bg-cream/10 border border-border rounded text-foreground placeholder-foreground-muted focus:outline-none focus:border-accent resize-none text-sm"
                />
              </div>

              <button
                onClick={handleSave}
                className="w-full px-4 py-2 bg-accent text-accent-foreground rounded hover:bg-accent-hover font-medium"
              >
                Save Technical Debt
              </button>
            </div>
          </div>
        )}

        {/* Prioritize View */}
        {viewMode === 'prioritize' && (
          <div className="space-y-4">
            <div className="p-4 bg-cream/5 border border-border rounded">
              <h3 className="text-lg font-bold text-foreground mb-2">ROI-Based Prioritization</h3>
              <p className="text-sm text-foreground-secondary">
                Items ranked by return on investment (business impact vs. effort)
              </p>
            </div>

            <div className="space-y-2">
              {prioritized.slice(0, 10).map(({ element, debt, roi }, index) => (
                <div key={element.id} className="p-4 bg-cream/5 border border-border rounded">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <div className="font-medium text-foreground">
                        #{index + 1} {element.name}
                      </div>
                      <div className="text-xs text-foreground-tertiary mt-1">
                        {element.type} • {element.layer}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-bold text-accent">ROI: {roi.toFixed(2)}</div>
                      <div className="flex gap-0.5 mt-1">
                        {[...Array(debt.priority)].map((_, i) => (
                          <span key={i} className="text-accent text-xs">⭐</span>
                        ))}
                      </div>
                    </div>
                  </div>
                  <div className="text-sm text-foreground/90 mb-2">{debt.description}</div>
                  <div className="flex items-center gap-4 text-xs text-foreground-tertiary">
                    <span className="capitalize">{debt.category}</span>
                    <span>•</span>
                    <span>Score: {debt.score}/10</span>
                    <span>•</span>
                    <span className="capitalize">Impact: {debt.businessImpact}</span>
                    <span>•</span>
                    <span>Effort: {debt.estimatedEffort}</span>
                  </div>
                </div>
              ))}
            </div>

            {prioritized.length === 0 && (
              <div className="p-8 text-center text-foreground-tertiary">
                <AlertTriangle className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No technical debt items found</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
